import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import i18n from '../../../i18n';
import User from '../../../models/User';

import Loading from '../../common/Loading';
import { Button, Callout, Collapsible, Flex, Link, Text } from '@homeexchange/design';
import { trackButtonClick } from '../../../utils/utils-ts/trackingUtils';

const handleReferralClick = (e) => {
    e.preventDefault();
    const href = i18n.t('url:sponsorship');
    trackButtonClick('GP_menu', 'Referral', undefined, () => {
        window.location.href = href;
    });
};

const displayNextSteps = (hasEverSubscribed, achievements, user, transfers) => {
    if (!achievements) {
        return null;
    }

    return (
        <Flex isFullWidth gap={'0'}>
            {!hasEverSubscribed && (
                <Text isBold className="achievement-title">
                    {i18n.t('global:gpMenu.nextSteps', { count: achievements.length })}
                </Text>
            )}
            {hasEverSubscribed && (
                <Text isBold className="achievement-title">
                    {i18n.t('global:gpMenu.getMore')}
                </Text>
            )}
            {displayCallout(hasEverSubscribed, transfers)}
            {achievements.map((achievement) => {
                let step_link = i18n.t(achievement.step_link);
                if (achievement.step_translated === 'gpMenu.completeHome') {
                    const homes = user.getHomesNotCompleted();
                    if (homes?.length > 0) {
                        step_link = homes[0].getEditUrl();
                    }
                }
                const isReferral = achievement.step_translated === 'gpMenu.referFriends';
                return (
                    <React.Fragment key={achievement.step_translated}>
                        <Flex isFullWidth flexGrow onClick={isReferral ? handleReferralClick : undefined}>
                            <Link href={isReferral ? i18n.t('url:sponsorship') : step_link} noUnderline>
                                <Flex justifyContent={'space-between'} isFullWidth>
                                    <Text>{i18n.t(`global:${achievement.step_translated}`)}</Text>
                                    <Text isSpan>
                                        <Text isBold isSpan>
                                            +{achievement.value}
                                        </Text>{' '}
                                        GP
                                    </Text>
                                </Flex>
                                {achievement.referral_bonus && (
                                    <Text
                                        size="sm"
                                        color="secondary"
                                        dangerouslySetInnerHTML={{
                                            __html: i18n.t('global:gpMenu.activateMembership.referralBonus', {
                                                nb: achievement.referral_bonus
                                            })
                                        }}
                                    />
                                )}
                            </Link>
                        </Flex>
                    </React.Fragment>
                );
            })}
        </Flex>
    );
};

const displayCallout = (hasEverSubscribed, transfers) => {
    if (hasEverSubscribed || !transfers || !transfers.gpRewardExpirationDate || !transfers.gpRewardExpireIn) {
        return null;
    }

    if (transfers.gpRewardExpireIn < 30) {
        return (
            <Callout status="warning">
                <Text isBold>
                    {i18n.t('global:gpMenu.calloutTitle.warning.v2', {
                        nb: transfers.gpRewardExpireIn,
                        gp: transfers.gpRewardBalanceToExpire
                    })}
                </Text>
                <Text size="sm">{i18n.t('global:gpMenu.calloutDesc.warning')}</Text>
            </Callout>
        );
    }

    return (
        <Callout status="info">
            <Text isBold>
                {i18n.t('global:gpMenu.calloutTitle.info.v2', {
                    date: moment(transfers.gpRewardExpirationDate).format('L'),
                    nb: transfers.gpRewardBalanceToExpire
                })}
            </Text>
            <Text size="sm">{i18n.t('global:gpMenu.calloutDesc')}</Text>
        </Callout>
    );
};

export const MenuGuestpoints = ({ user, transfers }) => {
    if (!user || !transfers) {
        return (
            <div
                className="dropdown-menu scrollable-menu moving-hover gp-menu"
                role="menu"
                id="guestpoint_elt"
            >
                <Loading />
            </div>
        );
    }

    const subscription = user.get('subscription');
    const hasEverSubscribed = subscription && subscription.active === true;
    const godsons = user.get('godsons');
    const hasReachedGodsonsLimit = godsons && godsons.length >= User.MAX_GODSONS_FOR_GP;

    return (
        <Flex gap={'4'}>
            <Flex gap={'2'}>
                <Flex justifyContent={'space-between'} isFullWidth>
                    <Text isBold>{i18n.t('global:gpMenu.yourGP')}</Text>
                    <Text isSpan>
                        <Text isBold isSpan color="tertiary">
                            {transfers.balance}
                        </Text>{' '}
                        GP
                    </Text>
                </Flex>
                {!hasReachedGodsonsLimit && (
                    <Flex isFullWidth>
                        <Collapsible
                            labelOpen={i18n.t('global:gpMenu.introGP.title')}
                            labelClosed={i18n.t('global:gpMenu.introGP.title')}
                            openOn={'bottom'}
                            isBorderless
                        >
                            <Text>{i18n.t('global:gpMenu.introGP.desc')}</Text>
                        </Collapsible>
                    </Flex>
                )}
            </Flex>
            {displayNextSteps(hasEverSubscribed, transfers.achievements, user, transfers)}
            <Button
                isFullWidth
                hierarchy="secondary"
                href={i18n.t('url:url.gp.history')}
                className="gp-menu-button"
            >
                {i18n.t('global:gpMenu.seeHistory.cta')}
            </Button>
        </Flex>
    );
};

MenuGuestpoints.propTypes = {
    user: PropTypes.instanceOf(User),
    transfers: PropTypes.objectOf(
        PropTypes.shape({
            balance: PropTypes.number,
            achievements: PropTypes.array,
            gpRewardBalanceToExpire: PropTypes.number,
            gpRewardExpirationDate: PropTypes.string,
            gpRewardExpireIn: PropTypes.number
        })
    )
};

const mapStateToProps = (state) => ({
    user: state.user,
    transfers: state.transfers
});

export default connect(mapStateToProps)(MenuGuestpoints);
